import type {
  PointerAction,
  PointerContext,
  PointerExplanation,
  PointerHelpSource,
} from './types';
import { collectContext } from './collect-context';

const THINK_MS = 250;

const FOCUS_EMPTY: PointerAction = {
  label: 'Take me to the next empty field',
  focus: '@empty',
};

function explainGeneric(context: PointerContext): PointerExplanation {
  const missing = context.fields.filter((field) => !field.filled);
  const missingText = missing.length
    ? ` Still needed: ${missing.map((field) => field.label).join(', ')}.`
    : '';
  const nextSteps = missing.length ? [FOCUS_EMPTY] : [];

  if (context.detail) {
    return {
      title: 'That did not work',
      summary: `${context.detail} Review what you entered and try again.${missingText}`,
      nextSteps,
    };
  }
  return {
    title: 'Need a hand?',
    summary: `${context.heading ? `You are on "${context.heading}". ` : ''}Fill in the form, then press the main button to continue.${missingText}`,
    nextSteps,
  };
}

export async function explainPointerTarget(
  detail: string | undefined,
  help: PointerHelpSource | undefined,
  signal: AbortSignal,
): Promise<PointerExplanation> {
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, THINK_MS);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });

  if (!help) return explainGeneric(collectContext('manual', detail));
  const resolved =
    typeof help === 'function' ? help(collectContext('manual', detail)) : help;
  return { ...resolved, nextSteps: resolved.nextSteps ?? [] };
}
